import { c as _c } from "react/compiler-runtime";
import type { DocumentUpload } from '@onefootprint/request-types/dashboard';
import { LinkButton } from '@onefootprint/ui';
import { cx } from 'class-variance-authority';
import { forwardRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { EntityVault } from '@/types/vault';
import type { Rotation } from '@/utils/rotation';
import { getUploadKey } from '../../utils/get-doc-upload-key';
import UploadImageItem from '../upload-image-item';
import UploadTitleCard from '../upload-title-card';
import getTimestampRange from './utils/get-timestamp-range';
export type FailedUploadsProps = {
  uploads: (DocumentUpload & {
    isLatest: boolean;
  })[];
  vault: EntityVault;
  rotations: Record<string, Rotation>;
  onRotate: (uploadKey: string) => void;
};
const FailedUploads = forwardRef<HTMLDivElement, FailedUploadsProps>((t0, ref) => {
  const $ = _c(41);
  const {
    uploads,
    vault,
    rotations,
    onRotate
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "fieldset.documents.details"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("entity-details", t1);
  const [isCollapsed, setIsCollapsed] = useState(true);
  if (uploads.length === 1) {
    let t2;
    if ($[1] !== uploads[0]) {
      t2 = getUploadKey(uploads[0]);
      $[1] = uploads[0];
      $[2] = t2;
    } else {
      t2 = $[2];
    }
    const uploadKey = t2;
    const t3 = rotations[uploadKey] ?? 0;
    let t4;
    if ($[3] !== onRotate || $[4] !== uploadKey) {
      t4 = () => onRotate(uploadKey);
      $[3] = onRotate;
      $[4] = uploadKey;
      $[5] = t4;
    } else {
      t4 = $[5];
    }
    let t5;
    if ($[6] !== ref || $[7] !== t3 || $[8] !== t4 || $[9] !== uploads[0] || $[10] !== vault) {
      t5 = <UploadImageItem upload={uploads[0]} vault={vault} ref={ref} rotation={t3} onRotate={t4} />;
      $[6] = ref;
      $[7] = t3;
      $[8] = t4;
      $[9] = uploads[0];
      $[10] = vault;
      $[11] = t5;
    } else {
      t5 = $[11];
    }
    return t5;
  }
  const t2 = uploads[0];
  let t3;
  if ($[12] !== uploads) {
    t3 = getTimestampRange(uploads);
    $[12] = uploads;
    $[13] = t3;
  } else {
    t3 = $[13];
  }
  let t4;
  if ($[14] !== t || $[15] !== uploads.length) {
    t4 = t(_temp, {
      count: uploads.length
    });
    $[14] = t;
    $[15] = uploads.length;
    $[16] = t4;
  } else {
    t4 = $[16];
  }
  let t5;
  if ($[17] !== t4) {
    t5 = <p className="text-label-2">{t4}</p>;
    $[17] = t4;
    $[18] = t5;
  } else {
    t5 = $[18];
  }
  let t6;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <span className="text-label-2">⋅</span>;
    $[19] = t6;
  } else {
    t6 = $[19];
  }
  let t7;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = () => setIsCollapsed(_temp2);
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  let t8;
  if ($[21] !== isCollapsed || $[22] !== t) {
    t8 = isCollapsed ? t(_temp3) : t(_temp4);
    $[21] = isCollapsed;
    $[22] = t;
    $[23] = t8;
  } else {
    t8 = $[23];
  }
  let t9;
  if ($[24] !== t8) {
    t9 = <LinkButton onClick={t7}>{t8}</LinkButton>;
    $[24] = t8;
    $[25] = t9;
  } else {
    t9 = $[25];
  }
  let t10;
  if ($[26] !== t5 || $[27] !== t9) {
    t10 = <div className="flex items-center gap-1">{t5}{t6}{t9}</div>;
    $[26] = t5;
    $[27] = t9;
    $[28] = t10;
  } else {
    t10 = $[28];
  }
  let t11;
  if ($[29] !== t10 || $[30] !== t3 || $[31] !== uploads[0]) {
    t11 = <UploadTitleCard upload={t2} customTimestamp={t3} rightChildren={t10} />;
    $[29] = t10;
    $[30] = t3;
    $[31] = uploads[0];
    $[32] = t11;
  } else {
    t11 = $[32];
  }
  let t12;
  if ($[33] !== isCollapsed || $[34] !== uploads || $[35] !== vault) {
    t12 = isCollapsed ? <div className="flex flex-col relative w-full top-3">{uploads.map((upload, index) => <UploadImageItem key={getUploadKey(upload)} upload={upload as DocumentUpload & {
        isLatest: boolean;
      }} vault={vault} rotateIndex={index} imageOnly={true} />)}</div> : <div className="grid w-full pl-4">{uploads.map((upload_0, index_0) => <LinedUploadContainer key={getUploadKey(upload_0)} isFirst={index_0 === 0} isLast={index_0 === uploads.length - 1}><UploadImageItem upload={upload_0} vault={vault} imageOnly={true} /></LinedUploadContainer>)}</div>;
    $[33] = isCollapsed;
    $[34] = uploads;
    $[35] = vault;
    $[36] = t12;
  } else {
    t12 = $[36];
  }
  let t13;
  if ($[37] !== ref || $[38] !== t11 || $[39] !== t12) {
    t13 = <div className="flex flex-col items-center w-full" ref={ref}>{t11}{t12}</div>;
    $[37] = ref;
    $[38] = t11;
    $[39] = t12;
    $[40] = t13;
  } else {
    t13 = $[40];
  }
  return t13;
});
const LinedUploadContainer = t0 => {
  const $ = _c(7);
  const {
    children,
    isFirst,
    isLast
  } = t0;
  const t1 = !isLast;
  let t2;
  if ($[0] !== isFirst || $[1] !== isLast || $[2] !== t1) {
    t2 = cx("relative pl-4 pt-6\n      before:content-[''] before:absolute before:left-0 before:top-0 before:w-[1px] before:bg-[#cecece]\n      after:content-[''] after:absolute after:left-0 after:w-4 after:h-4\n      after:border-l after:border-b after:border-solid after:border-primary\n      after:rounded-bl-xl", {
      "pt-2": isFirst,
      "before:h-[55%] after:top-[55%]": isLast,
      "before:h-full after:top-[50%]": t1
    });
    $[0] = isFirst;
    $[1] = isLast;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== children || $[5] !== t2) {
    t3 = <div className={t2}>{children}</div>;
    $[4] = children;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
};
export default FailedUploads;
function _temp($) {
  return $.titleCard.attempts;
}
function _temp2(prev) {
  return !prev;
}
function _temp3($_0) {
  return $_0.titleCard.showAll;
}
function _temp4($_1) {
  return $_1.titleCard.collapse;
}